import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom'; // <-- ADDED: Essential React Router imports
import { Phone, Send, Menu, X } from 'lucide-react'; // <-- ADDED: Menu, X icons for Header
import './Contact.css';
import './Home.css'; // <-- ADDED: Import shared CSS for Header and Footer

// --- Shared Header Data (Duplicated) ---
const navItems = [
    { id: 'home', label: 'Home', path: '/' }, 
    { id: 'about', label: 'About', path: '/about' },
    { id: 'services', label: 'Services', path: '/services' },
    { id: 'projects', label: 'Projects', path: '/projects' },
    { id: 'contact', label: 'Contact', path: '/contact' }
];

// Helper Component for Header content (Duplicated across all pages)
const HeaderContent = ({ isMobileMenuOpen, setIsMobileMenuOpen }) => {
    const location = useLocation();
    const getLinkClass = (path) => location.pathname === path ? 'active' : '';

    return (
        <header className="header">
            <div className="header-content">
                <img src="/assets/Construction Logo (4).png" alt="S&L Construction Logo" className="header-logo" />
                <Link to="/" className="logo">
                    S&L Construction, Inc.
                </Link>
                <nav className="nav-desktop">
                    {navItems.map(item => (
                        <Link 
                            key={item.id}
                            to={item.path} 
                            className={`nav-link ${getLinkClass(item.path)}`}
                        >
                            {item.label}
                        </Link>
                    ))}
                </nav>
                <a href="tel:8884642289" className="header-phone">
                    <Phone size={18} />
                    (312) 607-7476
                </a>
                <button
                    className="mobile-menu-button"
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                >
                    {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>
            <div className={`nav-mobile ${isMobileMenuOpen ? 'open' : ''}`}>
                {navItems.map(item => (
                    <Link 
                        key={item.id}
                        to={item.path}
                        className={`nav-link-mobile ${getLinkClass(item.path)}`}
                        onClick={() => setIsMobileMenuOpen(false)} 
                    >
                        {item.label}
                    </Link>
                ))}
                <a href="tel:8884642289" className="nav-phone-mobile">
                    <Phone size={18} />
                    (312) 607-7476
                </a>
            </div>
        </header>
    );
};
// --- End Shared Header Content ---

const Contact = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // <-- ADDED: Mobile menu state
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        projectType: '',
        details: ''
    });
    const [formStatus, setFormStatus] = useState('');
    const sectionRef = useRef(null);

    useEffect(() => {
        const currentRef = sectionRef.current;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, []);


    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = () => {
        if (!formData.name || !formData.email || !formData.phone) {
            setFormStatus('Please fill in all required fields');
            return;
        }
        
        if (!formData.projectType) {
            setFormStatus('Please select a project type');
            return;
        }
        
        // --- In a real app, you would send this data to a server here ---
        
        setFormStatus('Thank you! We will contact you soon.');
        
        setTimeout(() => {
            setFormData({
                name: '',
                email: '',
                phone: '',
                projectType: '',
                details: ''
            });
            setFormStatus('');
        }, 3000);
    };

    return (
        <div className="page-wrapper"> {/* <-- WRAPPER ADDED for full page structure */}
            {/* 1. HEADER SECTION */}
            <HeaderContent 
                isMobileMenuOpen={isMobileMenuOpen} 
                setIsMobileMenuOpen={setIsMobileMenuOpen} 
            />

            {/* 2. MAIN CONTENT (Your original Contact JSX) */}
            <main>
                <div className="contact-container" ref={sectionRef}>
                    <div className="contact-content">
                        <div className={`section-header ${isVisible ? 'animate-in' : ''}`}>
                            <span className="section-label">Get In Touch</span>
                            <h2 className="section-title">Let’s Build Something Extraordinary</h2>
                            <p className="section-subtitle">
                                The fastest way to get started is by giving us a call. Speak directly with our team to discuss your project.
                            </p>
                        </div>

                        <div className="contact-main">
                            <div className={`contact-info ${isVisible ? 'slide-in-left' : ''}`}>
                                <a href="tel:8884642289" className="cta-phone-large">
                                    <Phone size={24} />
                                    <div>
                                        <div className="cta-label">Call Us Now</div>
                                        <div className="cta-number">(312) 607-7476</div>
                                    </div>
                                </a>
                            </div>

                            <div className={`contact-form-wrapper ${isVisible ? 'slide-in-right' : ''}`}>
                                <div className="form-header">
                                    <h3>Prefer to Write Instead?</h3>
                                    <p>Use the form below and we’ll reach back to you.</p>
                                </div>

                                <div className="contact-form">
                                    <div className="form-group">
                                        <label htmlFor="name">Full Name *</label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            placeholder="John Doe"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="email">Email *</label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="your.email@example.com"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="phone">Phone *</label>
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            placeholder="(123) 456-7890"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="projectType">Project Type *</label>
                                        <select
                                            id="projectType"
                                            name="projectType"
                                            cd e={formData.projectType}
                                            onChange={handleChange}
                                        >
                                            <option value="remodeling">Remodeling</option>
                                            <option value="restoration">Restoration</option>
                                            <option value="interior/exterior">Interior/Exterior</option>
                                            <option value="new-build">New Construction</option>
                                            <option value="renovation">Renovation</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="details">Project Details</label>
                                        <textarea
                                            id="details"
                                            name="details"
                                            value={formData.details}
                                            onChange={handleChange}
                                            placeholder="Tell us about your project..."
                                            rows={5}
                                        ></textarea>
                                    </div>

                                    {formStatus && (
                                        <div className={`form-status ${formStatus.includes('Thank') ? 'success' : 'error'}`}>
                                            {formStatus}
                                        </div>
                                    )}

                                    <button type="button" onClick={handleSubmit} className="submit-button">
                                        <Send size={20} />
                                        Send Request
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            
            {/* 3. FOOTER SECTION */}
            <footer className="footer-section">
                <div className="footer-content">
                    <p className="footer-copy">© 2025 S&L Construction, Inc. | All Rights Reserved</p>
                    <a href="tel:8884642289" className="footer-phone">
                        <Phone size={16} />
                        Call Us: (312) 607-7476
                    </a>
                </div>
            </footer>
        </div>
    );
};

export default Contact;